import { MVELCompletionItem } from '@/src/components/mvel-editor/types';
import { MVELSchema } from '@/src/types/mvel.types';
import * as monaco from 'monaco-editor';

/**
 * Enhanced MVEL Language Support - Complete MVEL 2.0 Implementation
 *
 * PURPOSE: Provides comprehensive MVEL language support for Monaco Editor
 * WHY: Enables IDE-like experience with full MVEL feature coverage
 * HOW: Multi-layered language definition with complete syntax and semantic support
 *
 * FEATURES IMPLEMENTED:
 * - Complete MVEL 2.0 syntax highlighting
 * - Comprehensive code completion
 * - All MVEL operators and keywords
 * - Built-in functions and methods
 * - Template engine support
 * - Type system integration
 * - Advanced language constructs
 */

// MVEL language configuration with enhanced bracket and comment support
export const mvelLanguageConfig: monaco.languages.LanguageConfiguration = {
  comments: {
    lineComment: '//',
    blockComment: ['/*', '*/']
  },
  brackets: [
    ['{', '}'], // Code blocks, object literals
    ['[', ']'], // Arrays, maps, property access
    ['(', ')'] // Method calls, expressions
  ],
  autoClosingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: '"', close: '"' },
    { open: "'", close: "'" },
    { open: '`', close: '`' } // Template strings
  ],
  surroundingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: '"', close: '"' },
    { open: "'", close: "'" },
    { open: '`', close: '`' }
  ],
  // Enhanced folding support
  folding: {
    markers: {
      start: /^\s*\/\*\*/,
      end: /^\s*\*\//
    }
  }
};

// Enhanced MVEL token provider with complete MVEL 2.0 support
export const mvelTokenProvider: monaco.languages.IMonarchLanguage = {
  // Complete MVEL keyword set based on official documentation
  keywords: [
    // Control flow
    'if',
    'else',
    'for',
    'foreach',
    'while',
    'until',
    'do',
    'break',
    'continue',
    // Function definition
    'function',
    'def',
    'return',
    // Object creation and type checking
    'new',
    'instanceof',
    'isdef',
    'is',
    // Literals and constants
    'null',
    'nil',
    'true',
    'false',
    'empty',
    // Variable declaration
    'var',
    'this',
    'super',
    // Import and package
    'import',
    'package',
    // Exception handling
    'try',
    'catch',
    'finally',
    'throw',
    // Template engine keywords
    'include',
    'declare',
    'eval',
    // Type declarations
    'int',
    'Integer',
    'long',
    'Long',
    'float',
    'Float',
    'double',
    'Double',
    'boolean',
    'Boolean',
    'char',
    'Character',
    'byte',
    'Byte',
    'short',
    'Short',
    'String',
    'Object',
    'Map',
    'List',
    'Set',
    'Collection',
    // Advanced constructs
    'synchronized',
    'with',
    'assert',
    'untyped'
  ],

  // Complete operator set including MVEL-specific operators
  operators: [
    // Arithmetic
    '=',
    '>',
    '<',
    '!',
    '~',
    '?',
    ':',
    '==',
    '<=',
    '>=',
    '!=',
    '&&',
    '||',
    '++',
    '--',
    '+',
    '-',
    '*',
    '/',
    '&',
    '|',
    '^',
    '%',
    '<<',
    '>>',
    '>>>',
    '+=',
    '-=',
    '*=',
    '/=',
    '&=',
    '|=',
    '^=',
    '%=',
    '<<=',
    '>>=',
    '>>>=',
    // MVEL-specific operators
    '?:',
    '?.',
    '~=',
    'soundslike',
    'strsim',
    'in',
    'contains',
    // Projection operators
    'in',
    '!in',
    // Regular expression
    '~',
    '!~'
  ],

  // Enhanced symbol recognition
  symbols: /[=><!~?:&|+\-*\/\^%#@]+/,
  escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,
  digits: /\d+(_+\d+)*/,
  octaldigits: /[0-7]+(_+[0-7]+)*/,
  binarydigits: /[0-1]+(_+[0-1]+)*/,
  hexdigits: /[[0-9a-fA-F]+(_+[0-9a-fA-F]+)*/,

  tokenizer: {
    root: [
      // Enhanced identifier recognition
      [
        /[a-zA-Z_$][\w$]*/,
        {
          cases: {
            '@keywords': 'keyword',
            '@default': 'identifier'
          }
        }
      ],

      // Type identifiers (capitalized)
      [/[A-Z][\w\$]*/, 'type.identifier'],

      // Template orb tags (@{...})
      [/@\{/, { token: 'delimiter.bracket', next: '@orb' }],

      // Template tags (@code{...}, @if{...}, etc.)
      [
        /@(code|if|else|foreach|include|declare|comment)\{/,
        { token: 'keyword.template', next: '@template' }
      ],

      // Include whitespace handling
      { include: '@whitespace' },

      // Enhanced bracket support
      [/[{}()\[\]]/, '@brackets'],
      [/[<>](?!@symbols)/, '@brackets'],

      // Operator recognition
      [
        /@symbols/,
        {
          cases: {
            '@operators': 'operator',
            '@default': ''
          }
        }
      ],

      // Enhanced number recognition
      [/(@digits)[eE]([\-+]?(@digits))?[fFdD]?/, 'number.float'],
      [/(@digits)\.(@digits)([eE][\-+]?(@digits))?[fFdD]?/, 'number.float'],
      [/0[xX](@hexdigits)[Ll]?/, 'number.hex'],
      [/0(@octaldigits)[Ll]?/, 'number.octal'],
      [/0[bB](@binarydigits)[Ll]?/, 'number.binary'],
      [/(@digits)[fFdD]/, 'number.float'],
      [/(@digits)[lL]?/, 'number'],

      // BigInteger and BigDecimal literals
      [/(@digits)[bB]/, 'number.bigdecimal'],
      [/(@digits)[iI]/, 'number.biginteger'],

      // Delimiters
      [/[;,.]/, 'delimiter'],

      // Enhanced string support
      [/"([^"\\]|\\.)*$/, 'string.invalid'],
      [/"/, { token: 'string.quote', bracket: '@open', next: '@string' }],
      [/'([^'\\]|\\.)*$/, 'string.invalid'],
      [/'/, { token: 'string.quote', bracket: '@open', next: '@string_single' }],

      // Character literals
      [/'[^\\']'/, 'string'],
      [/(')(@escapes)(')/, ['string', 'string.escape', 'string']],
      [/'/, 'string.invalid'],

      // Regular expressions
      [/\/(?=([^\/\\]|\\.)+\/([gimx]*)(\s|[);.,}]|$))/, { token: 'regexp', next: '@regexp' }]
    ],

    // Template orb handling (@{...})
    orb: [
      [/[^\}]+/, 'expression'],
      [/\}/, { token: 'delimiter.bracket', next: '@pop' }]
    ],

    // Template tag handling
    template: [
      [/[^\}]+/, 'template.content'],
      [/\}/, { token: 'keyword.template', next: '@pop' }]
    ],

    // Enhanced comment handling
    comment: [
      [/[^\/*]+/, 'comment'],
      [/\/\*/, 'comment', '@push'],
      ['\\*/', 'comment', '@pop'],
      [/[\/*]/, 'comment']
    ],

    // Enhanced string handling with escape sequences
    string: [
      [/[^\\"]+/, 'string'],
      [/@escapes/, 'string.escape'],
      [/\\./, 'string.escape.invalid'],
      [/"/, { token: 'string.quote', bracket: '@close', next: '@pop' }]
    ],

    string_single: [
      [/[^\\']+/, 'string'],
      [/@escapes/, 'string.escape'],
      [/\\./, 'string.escape.invalid'],
      [/'/, { token: 'string.quote', bracket: '@close', next: '@pop' }]
    ],

    // Regular expression handling
    regexp: [
      [
        /(\{)(\d+(?:,\d*)?)(\})/,
        ['regexp.escape.control', 'regexp.escape.control', 'regexp.escape.control']
      ],
      [
        /(\[)(\^?)(?=(?:[^\]\\\/]|\\.)+)/,
        ['regexp.escape.control', { token: 'regexp.escape.control', next: '@regexrange' }]
      ],
      [/(\()(\?:|\?=|\?!)/, ['regexp.escape.control', 'regexp.escape.control']],
      [/[()]/, 'regexp.escape.control'],
      [/@regexpctl/, 'regexp.escape.control'],
      [/[^\\\/]/, 'regexp'],
      [/@regexpesc/, 'regexp.escape'],
      [/\\\./, 'regexp.invalid'],
      [/(\/)([gimx]*)/, { token: 'regexp', next: '@pop' }]
    ],

    regexrange: [
      [/-/, 'regexp.escape.control'],
      [/\^/, 'regexp.invalid'],
      [/@regexpesc/, 'regexp.escape'],
      [/[^\]]/, 'regexp'],
      [/\]/, { token: 'regexp.escape.control', next: '@pop' }]
    ],

    whitespace: [
      [/[ \t\r\n]+/, 'white'],
      [/\/\*/, 'comment', '@comment'],
      [/\/\/.*$/, 'comment']
    ]
  },

  // Regular expression controls
  regexpctl: /[(){}\[\]\$\^|\-*+?\.]/,
  regexpesc: /\\(?:[bBdDfnrstvwWn0\\\/]|@regexpctl|c[A-Z]|x[0-9a-fA-F]{2}|u[0-9a-fA-F]{4})/
};

// Complete MVEL built-in functions and constructs
export const mvelBuiltinsEnhanced: MVELCompletionItem[] = [
  // === CORE LITERALS ===
  {
    label: 'true',
    kind: 'Keyword',
    insertText: 'true',
    detail: 'Boolean true literal',
    documentation: 'Boolean true value - represents logical true'
  },
  {
    label: 'false',
    kind: 'Keyword',
    insertText: 'false',
    detail: 'Boolean false literal',
    documentation: 'Boolean false value - represents logical false'
  },
  {
    label: 'null',
    kind: 'Keyword',
    insertText: 'null',
    detail: 'Null literal',
    documentation: 'Represents a null value - absence of any object value'
  },
  {
    label: 'nil',
    kind: 'Keyword',
    insertText: 'nil',
    detail: 'Null literal (alternative syntax)',
    documentation: 'Alternative null literal - same as null, for convenience'
  },
  {
    label: 'empty',
    kind: 'Keyword',
    insertText: 'empty',
    detail: 'Empty test literal',
    documentation:
      'Tests if a value is empty: null, empty string, empty collection, or array with length 0'
  },

  // === CONTROL FLOW ===
  {
    label: 'if',
    kind: 'Keyword',
    insertText: 'if (${1:condition}) {\n\t${2:// code}\n}',
    detail: 'Conditional if statement',
    documentation: 'C/Java style if-then conditional execution. Supports else and else-if branches.'
  },
  {
    label: 'if-else',
    kind: 'Snippet',
    insertText: 'if (${1:condition}) {\n\t${2:// if true}\n} else {\n\t${3:// if false}\n}',
    detail: 'If-else statement',
    documentation: 'Complete if-else conditional structure'
  },
  {
    label: 'if-else-if',
    kind: 'Snippet',
    insertText:
      'if (${1:condition1}) {\n\t${2:// first condition}\n} else if (${3:condition2}) {\n\t${4:// second condition}\n} else {\n\t${5:// default case}\n}',
    detail: 'If-else-if chain',
    documentation: 'Multiple condition chain with else-if branches'
  },
  {
    label: 'ternary',
    kind: 'Snippet',
    insertText: '${1:condition} ? ${2:trueValue} : ${3:falseValue}',
    detail: 'Ternary conditional operator',
    documentation: 'Inline conditional: condition ? valueIfTrue : valueIfFalse'
  },

  // === LOOPS ===
  {
    label: 'foreach',
    kind: 'Keyword',
    insertText: 'foreach (${1:item} : ${2:collection}) {\n\t${3:// process item}\n}',
    detail: 'Foreach loop over collection',
    documentation:
      'Iterate over collections, arrays, or strings. Syntax: foreach (var : collection)'
  },
  {
    label: 'foreach-count',
    kind: 'Snippet',
    insertText: 'foreach (${1:i} : ${2:10}) {\n\t${3:// count from 1 to 10}\n}',
    detail: 'Foreach count loop',
    documentation: 'Count up to a number: foreach (x : 9) counts from 1 to 9'
  },
  {
    label: 'for',
    kind: 'Keyword',
    insertText: 'for (${1:item} : ${2:collection}) {\n\t${3:// process item}\n}',
    detail: 'For-each loop (MVEL 2.0+ alias)',
    documentation: 'Abbreviated foreach syntax introduced in MVEL 2.0'
  },
  {
    label: 'for-c-style',
    kind: 'Snippet',
    insertText: 'for (${1:int i = 0}; ${2:i < 100}; ${3:i++}) {\n\t${4:// loop body}\n}',
    detail: 'C-style for loop',
    documentation: 'Traditional C/Java for loop: for(init; condition; increment)'
  },
  {
    label: 'while',
    kind: 'Keyword',
    insertText: 'while (${1:condition}) {\n\t${2:// loop body}\n}',
    detail: 'While loop',
    documentation: 'Execute while condition is true'
  },
  {
    label: 'until',
    kind: 'Keyword',
    insertText: 'until (${1:condition}) {\n\t${2:// loop body}\n}',
    detail: 'Until loop',
    documentation: 'Execute until condition becomes true (inverse of while)'
  },
  {
    label: 'do-while',
    kind: 'Snippet',
    insertText: 'do {\n\t${1:// loop body}\n} while (${2:condition});',
    detail: 'Do-while loop',
    documentation: 'Execute code block, then continue while condition is true'
  },
  {
    label: 'do-until',
    kind: 'Snippet',
    insertText: 'do {\n\t${1:// loop body}\n} until (${2:condition});',
    detail: 'Do-until loop',
    documentation: 'Execute code block, then continue until condition becomes true'
  },

  // === FUNCTION DEFINITION ===
  {
    label: 'def',
    kind: 'Keyword',
    insertText:
      'def ${1:functionName}(${2:parameters}) {\n\t${3:// function body}\n\t${4:returnValue};\n}',
    detail: 'Function definition',
    documentation: 'Define a function using def keyword. Functions support closures and parameters.'
  },
  {
    label: 'function',
    kind: 'Keyword',
    insertText:
      'function ${1:functionName}(${2:parameters}) {\n\t${3:// function body}\n\t${4:returnValue};\n}',
    detail: 'Function definition (alternative)',
    documentation: 'Alternative function definition syntax'
  },
  {
    label: 'return',
    kind: 'Keyword',
    insertText: 'return ${1:value};',
    detail: 'Return statement',
    documentation: 'Return a value from function. Optional due to last-value-out principle.'
  },
  {
    label: 'lambda',
    kind: 'Snippet',
    insertText: 'def (${1:parameters}) { ${2:expression} }',
    detail: 'Lambda expression',
    documentation: 'Anonymous function: def (x) { x >= 10 ? x : 0 }'
  },

  // === COLLECTION LITERALS ===
  {
    label: 'list',
    kind: 'Snippet',
    insertText: '[${1:"item1"}, ${2:"item2"}]',
    detail: 'List literal',
    documentation: 'Create a list: ["item1", "item2", "item3"]'
  },
  {
    label: 'map',
    kind: 'Snippet',
    insertText: '[${1:"key1"} : ${2:"value1"}, ${3:"key2"} : ${4:"value2"}]',
    detail: 'Map literal',
    documentation: 'Create a map: ["key1" : "value1", "key2" : "value2"]'
  },
  {
    label: 'array',
    kind: 'Snippet',
    insertText: '{${1:"item1"}, ${2:"item2"}}',
    detail: 'Array literal',
    documentation: 'Create an array: {"item1", "item2", "item3"}'
  },

  // === OPERATORS AND EXPRESSIONS ===
  {
    label: 'projection',
    kind: 'Snippet',
    insertText: '(${1:property} in ${2:collection})',
    detail: 'Projection operator',
    documentation: 'Extract property from collection: (name in users) gets all names'
  },
  {
    label: 'new',
    kind: 'Keyword',
    insertText: 'new ${1:ClassName}(${2:parameters})',
    detail: 'Object instantiation',
    documentation: 'Create new object instance: new HashMap(), new ArrayList()'
  },
  {
    label: 'instanceof',
    kind: 'Keyword',
    insertText: 'instanceof ${1:Type}',
    detail: 'Instance type check',
    documentation: 'Check if object is instance of type: obj instanceof String'
  },
  {
    label: 'isdef',
    kind: 'Keyword',
    insertText: 'isdef ${1:variable}',
    detail: 'Variable definition check',
    documentation: 'Check if variable is defined: isdef someVar'
  },
  {
    label: 'soundslike',
    kind: 'Keyword',
    insertText: 'soundslike ${1:"comparison"}',
    detail: 'Sounds-like operator',
    documentation: 'Phonetic string comparison: "John" soundslike "Jon"'
  },
  {
    label: 'strsim',
    kind: 'Keyword',
    insertText: 'strsim ${1:"comparison"}',
    detail: 'String similarity operator',
    documentation: 'String similarity comparison: returns similarity score'
  },
  {
    label: 'contains',
    kind: 'Keyword',
    insertText: 'contains ${1:value}',
    detail: 'Contains check',
    documentation: 'Check if collection/string contains value'
  },

  // === VARIABLE DECLARATION ===
  {
    label: 'var',
    kind: 'Keyword',
    insertText: 'var ${1:variableName} = ${2:value};',
    detail: 'Variable declaration',
    documentation: 'Declare a dynamically typed variable'
  },
  {
    label: 'typed-var',
    kind: 'Snippet',
    insertText: '${1:Type} ${2:variableName} = ${3:value};',
    detail: 'Typed variable declaration',
    documentation: 'Declare statically typed variable: String name = "John";'
  },

  // === TEMPLATE ENGINE ===
  {
    label: '@{}',
    kind: 'Snippet',
    insertText: '@{${1:expression}}',
    detail: 'Template expression orb',
    documentation: 'Template expression: @{user.name} evaluates and outputs expression'
  },
  {
    label: '@code{}',
    kind: 'Snippet',
    insertText: '@code{${1:// silent code}}',
    detail: 'Silent code template tag',
    documentation: 'Execute code without output: @code{var x = 10;}'
  },
  {
    label: '@if{}',
    kind: 'Snippet',
    insertText: '@if{${1:condition}}\n\t${2:content}\n@end{}',
    detail: 'Template if statement',
    documentation: 'Template conditional: @if{user.isAdmin} Admin Content @end{}'
  },
  {
    label: '@foreach{}',
    kind: 'Snippet',
    insertText: '@foreach{${1:item} : ${2:collection}}\n\t${3:@{item}}\n@end{}',
    detail: 'Template foreach loop',
    documentation: 'Template iteration: @foreach{user : users} @{user.name} @end{}'
  },
  {
    label: '@include{}',
    kind: 'Snippet',
    insertText: '@include{${1:"filename.mv"}}',
    detail: 'Include template file',
    documentation: 'Include external template: @include{"header.mv"}'
  },
  {
    label: '@declare{}',
    kind: 'Snippet',
    insertText: '@declare{${1:"templateName"}}\n\t${2:template content}\n@end{}',
    detail: 'Declare named template',
    documentation: 'Declare reusable template: @declare{"myTemplate"} content @end{}'
  },

  // === NULL-SAFE OPERATIONS ===
  {
    label: '?.',
    kind: 'Operator',
    insertText: '?.${1:property}',
    detail: 'Null-safe property access',
    documentation: 'Safe navigation: user.?manager.name avoids NPE if manager is null'
  },
  {
    label: '?:',
    kind: 'Operator',
    insertText: '?: ${1:defaultValue}',
    detail: 'Null coalescing operator',
    documentation: 'Provide default for null: name ?: "Unknown"'
  },

  // === STRING OPERATIONS ===
  {
    label: 'regex-match',
    kind: 'Snippet',
    insertText: '${1:string} ~= /${2:pattern}/',
    detail: 'Regular expression match',
    documentation: 'Regex matching: str ~= /\\d+/ checks if string contains digits'
  },
  {
    label: 'string-index',
    kind: 'Snippet',
    insertText: '${1:string}[${2:index}]',
    detail: 'String character access',
    documentation: 'Access character by index: "Hello"[0] returns "H"'
  },

  // === SYSTEM FUNCTIONS ===
  {
    label: 'System.out.println',
    kind: 'Function',
    insertText: 'System.out.println(${1:message});',
    detail: 'Print to console',
    documentation: 'Print message to console output'
  },
  {
    label: 'assert',
    kind: 'Keyword',
    insertText: 'assert ${1:condition};',
    detail: 'Assertion statement',
    documentation: 'Assert condition is true: assert x > 0;'
  },

  // === TYPE SYSTEM ===
  {
    label: 'String',
    kind: 'Type',
    insertText: 'String',
    detail: 'String type',
    documentation: 'String data type for text values'
  },
  {
    label: 'Integer',
    kind: 'Type',
    insertText: 'Integer',
    detail: 'Integer type',
    documentation: 'Integer wrapper type'
  },
  {
    label: 'Boolean',
    kind: 'Type',
    insertText: 'Boolean',
    detail: 'Boolean type',
    documentation: 'Boolean wrapper type'
  },
  {
    label: 'Map',
    kind: 'Type',
    insertText: 'Map',
    detail: 'Map type',
    documentation: 'Map interface for key-value pairs'
  },
  {
    label: 'List',
    kind: 'Type',
    insertText: 'List',
    detail: 'List type',
    documentation: 'List interface for ordered collections'
  },
  {
    label: 'Set',
    kind: 'Type',
    insertText: 'Set',
    detail: 'Set type',
    documentation: 'Set interface for unique collections'
  }
];

// Enhanced method completions with full coverage
export const enhancedArrayMethods: MVELCompletionItem[] = [
  {
    label: 'add',
    kind: 'Method',
    insertText: 'add(${1:element})',
    detail: 'Add element to collection',
    documentation: 'Add element to array/list: array.add("newItem")'
  },
  {
    label: 'addAll',
    kind: 'Method',
    insertText: 'addAll(${1:collection})',
    detail: 'Add all elements from collection',
    documentation: 'Add all elements from another collection'
  },
  {
    label: 'remove',
    kind: 'Method',
    insertText: 'remove(${1:index})',
    detail: 'Remove element at index',
    documentation: 'Remove element at specified index: array.remove(0)'
  },
  {
    label: 'removeAll',
    kind: 'Method',
    insertText: 'removeAll(${1:collection})',
    detail: 'Remove all matching elements',
    documentation: 'Remove all elements that exist in specified collection'
  },
  {
    label: 'get',
    kind: 'Method',
    insertText: 'get(${1:index})',
    detail: 'Get element at index',
    documentation: 'Get element at specified index: array.get(0)'
  },
  {
    label: 'set',
    kind: 'Method',
    insertText: 'set(${1:index}, ${2:element})',
    detail: 'Set element at index',
    documentation: 'Set element at specified index: array.set(0, "newValue")'
  },
  {
    label: 'size',
    kind: 'Method',
    insertText: 'size()',
    detail: 'Get collection size',
    documentation: 'Get number of elements in collection'
  },
  {
    label: 'isEmpty',
    kind: 'Method',
    insertText: 'isEmpty()',
    detail: 'Check if collection is empty',
    documentation: 'Returns true if collection has no elements'
  },
  {
    label: 'contains',
    kind: 'Method',
    insertText: 'contains(${1:element})',
    detail: 'Check if contains element',
    documentation: 'Check if collection contains specified element'
  },
  {
    label: 'containsAll',
    kind: 'Method',
    insertText: 'containsAll(${1:collection})',
    detail: 'Check if contains all elements',
    documentation: 'Check if collection contains all elements from another collection'
  },
  {
    label: 'indexOf',
    kind: 'Method',
    insertText: 'indexOf(${1:element})',
    detail: 'Find index of element',
    documentation: 'Find first index of element, returns -1 if not found'
  },
  {
    label: 'lastIndexOf',
    kind: 'Method',
    insertText: 'lastIndexOf(${1:element})',
    detail: 'Find last index of element',
    documentation: 'Find last index of element, returns -1 if not found'
  },
  {
    label: 'clear',
    kind: 'Method',
    insertText: 'clear()',
    detail: 'Clear all elements',
    documentation: 'Remove all elements from collection'
  },
  {
    label: 'toArray',
    kind: 'Method',
    insertText: 'toArray()',
    detail: 'Convert to array',
    documentation: 'Convert collection to array'
  },
  {
    label: 'subList',
    kind: 'Method',
    insertText: 'subList(${1:fromIndex}, ${2:toIndex})',
    detail: 'Get sublist',
    documentation: 'Get portion of list from fromIndex to toIndex'
  }
];

export const enhancedMapMethods: MVELCompletionItem[] = [
  {
    label: 'put',
    kind: 'Method',
    insertText: 'put(${1:key}, ${2:value})',
    detail: 'Put key-value pair',
    documentation: 'Add or update key-value pair: map.put("key", "value")'
  },
  {
    label: 'putAll',
    kind: 'Method',
    insertText: 'putAll(${1:map})',
    detail: 'Put all entries from map',
    documentation: 'Add all key-value pairs from another map'
  },
  {
    label: 'get',
    kind: 'Method',
    insertText: 'get(${1:key})',
    detail: 'Get value by key',
    documentation: 'Get value for specified key: map.get("key")'
  },
  {
    label: 'getOrDefault',
    kind: 'Method',
    insertText: 'getOrDefault(${1:key}, ${2:defaultValue})',
    detail: 'Get value or default',
    documentation: 'Get value for key, or return default if key not found'
  },
  {
    label: 'remove',
    kind: 'Method',
    insertText: 'remove(${1:key})',
    detail: 'Remove key-value pair',
    documentation: 'Remove entry for specified key'
  },
  {
    label: 'containsKey',
    kind: 'Method',
    insertText: 'containsKey(${1:key})',
    detail: 'Check if contains key',
    documentation: 'Check if map contains specified key'
  },
  {
    label: 'containsValue',
    kind: 'Method',
    insertText: 'containsValue(${1:value})',
    detail: 'Check if contains value',
    documentation: 'Check if map contains specified value'
  },
  {
    label: 'keySet',
    kind: 'Method',
    insertText: 'keySet()',
    detail: 'Get all keys',
    documentation: 'Get set of all keys in map'
  },
  {
    label: 'values',
    kind: 'Method',
    insertText: 'values()',
    detail: 'Get all values',
    documentation: 'Get collection of all values in map'
  },
  {
    label: 'entrySet',
    kind: 'Method',
    insertText: 'entrySet()',
    detail: 'Get all entries',
    documentation: 'Get set of all key-value entries'
  },
  {
    label: 'size',
    kind: 'Method',
    insertText: 'size()',
    detail: 'Get map size',
    documentation: 'Get number of key-value pairs'
  },
  {
    label: 'isEmpty',
    kind: 'Method',
    insertText: 'isEmpty()',
    detail: 'Check if map is empty',
    documentation: 'Returns true if map has no entries'
  },
  {
    label: 'clear',
    kind: 'Method',
    insertText: 'clear()',
    detail: 'Clear all entries',
    documentation: 'Remove all key-value pairs from map'
  },
  {
    label: 'replace',
    kind: 'Method',
    insertText: 'replace(${1:key}, ${2:value})',
    detail: 'Replace value for key',
    documentation: 'Replace value for existing key'
  },
  {
    label: 'replaceAll',
    kind: 'Method',
    insertText: 'replaceAll(${1:function})',
    detail: 'Replace all values',
    documentation: 'Replace all values using a function'
  }
];

export const enhancedStringMethods: MVELCompletionItem[] = [
  {
    label: 'substring',
    kind: 'Method',
    insertText: 'substring(${1:start}, ${2:end})',
    detail: 'Get substring',
    documentation: 'Extract substring from start to end index'
  },
  {
    label: 'substr',
    kind: 'Method',
    insertText: 'substr(${1:start}, ${2:length})',
    detail: 'Get substring by length',
    documentation: 'Extract substring starting at index with specified length'
  },
  {
    label: 'indexOf',
    kind: 'Method',
    insertText: 'indexOf(${1:str})',
    detail: 'Find index of substring',
    documentation: 'Find first occurrence of substring'
  },
  {
    label: 'lastIndexOf',
    kind: 'Method',
    insertText: 'lastIndexOf(${1:str})',
    detail: 'Find last index of substring',
    documentation: 'Find last occurrence of substring'
  },
  {
    label: 'toLowerCase',
    kind: 'Method',
    insertText: 'toLowerCase()',
    detail: 'Convert to lowercase',
    documentation: 'Convert all characters to lowercase'
  },
  {
    label: 'toUpperCase',
    kind: 'Method',
    insertText: 'toUpperCase()',
    detail: 'Convert to uppercase',
    documentation: 'Convert all characters to uppercase'
  },
  {
    label: 'trim',
    kind: 'Method',
    insertText: 'trim()',
    detail: 'Remove whitespace',
    documentation: 'Remove leading and trailing whitespace'
  },
  {
    label: 'replace',
    kind: 'Method',
    insertText: 'replace(${1:oldStr}, ${2:newStr})',
    detail: 'Replace substring',
    documentation: 'Replace all occurrences of old string with new string'
  },
  {
    label: 'replaceAll',
    kind: 'Method',
    insertText: 'replaceAll(${1:regex}, ${2:replacement})',
    detail: 'Replace with regex',
    documentation: 'Replace all matches of regex with replacement'
  },
  {
    label: 'replaceFirst',
    kind: 'Method',
    insertText: 'replaceFirst(${1:regex}, ${2:replacement})',
    detail: 'Replace first match',
    documentation: 'Replace first match of regex with replacement'
  },
  {
    label: 'split',
    kind: 'Method',
    insertText: 'split(${1:delimiter})',
    detail: 'Split string',
    documentation: 'Split string into array using delimiter'
  },
  {
    label: 'matches',
    kind: 'Method',
    insertText: 'matches(${1:regex})',
    detail: 'Check regex match',
    documentation: 'Check if entire string matches regex pattern'
  },
  {
    label: 'startsWith',
    kind: 'Method',
    insertText: 'startsWith(${1:prefix})',
    detail: 'Check if starts with',
    documentation: 'Check if string starts with specified prefix'
  },
  {
    label: 'endsWith',
    kind: 'Method',
    insertText: 'endsWith(${1:suffix})',
    detail: 'Check if ends with',
    documentation: 'Check if string ends with specified suffix'
  },
  {
    label: 'charAt',
    kind: 'Method',
    insertText: 'charAt(${1:index})',
    detail: 'Get character at index',
    documentation: 'Get character at specified index'
  },
  {
    label: 'length',
    kind: 'Method',
    insertText: 'length()',
    detail: 'Get string length',
    documentation: 'Get number of characters in string'
  },
  {
    label: 'isEmpty',
    kind: 'Method',
    insertText: 'isEmpty()',
    detail: 'Check if string is empty',
    documentation: 'Returns true if string has no characters'
  },
  {
    label: 'contains',
    kind: 'Method',
    insertText: 'contains(${1:str})',
    detail: 'Check if contains substring',
    documentation: 'Check if string contains specified substring'
  },
  {
    label: 'concat',
    kind: 'Method',
    insertText: 'concat(${1:str})',
    detail: 'Concatenate strings',
    documentation: 'Concatenate this string with another string'
  },
  {
    label: 'valueOf',
    kind: 'Method',
    insertText: 'valueOf(${1:value})',
    detail: 'Convert to string',
    documentation: 'Convert value to string representation'
  }
];

/**
 * Enhanced method provider with complete MVEL support
 *
 * WHAT: Returns appropriate method completions based on data type
 * WHY: Provides context-aware intellisense for different data types
 * HOW: Type-based method filtering with comprehensive coverage
 */
export function getMethodsForTypeEnhanced(type: string): MVELCompletionItem[] {
  switch (type.toLowerCase()) {
    case 'array':
    case 'list':
    case 'collection':
    case 'set':
      return enhancedArrayMethods;
    case 'map':
    case 'object':
    case 'hashmap':
    case 'linkedhashmap':
    case 'treemap':
      return enhancedMapMethods;
    case 'string':
    case 'text':
      return enhancedStringMethods;
    default:
      return [];
  }
}

/**
 * Enhanced schema-based completion provider
 *
 * WHAT: Generates intelligent completions from JSON schema definitions
 * WHY: Provides accurate property suggestions based on data structure
 * HOW: Recursive schema traversal with nested property support
 */
export function createCompletionFromSchemaEnhanced(
  schema: MVELSchema,
  prefix = ''
): MVELCompletionItem[] {
  const completions: MVELCompletionItem[] = [];

  if (schema.type === 'object' && schema.properties) {
    Object.entries(schema.properties).forEach(([key, value]) => {
      const fullPath = prefix ? `${prefix}.${key}` : key;
      const isRequired = schema.required?.includes(key) || false;

      completions.push({
        label: key,
        kind: 'Property',
        insertText: key,
        detail: `${value.type}${isRequired ? ' (required)' : ''}${value.description ? ` - ${value.description}` : ''}`,
        documentation: `Property: ${fullPath} (${value.type})${value.description ? `\n\n${value.description}` : ''}`
      });

      // Add nested properties for objects
      if (value.type === 'object' && value.properties) {
        const nestedCompletions = createCompletionFromSchemaEnhanced(value, fullPath);
        completions.push(...nestedCompletions);
      }
    });
  }

  return completions;
}

export function getPropertiesForPath(path: string, schema?: any): MVELCompletionItem[] {
  if (!schema?.properties) return [];

  const parts = path.split('.');
  let current = schema.properties;

  // Navigate to the correct schema level
  for (const part of parts) {
    if (!current[part]) return [];
    current = current[part];

    if (current.type === 'object' && current.properties) {
      current = current.properties;
    } else {
      return [];
    }
  }

  // Return properties at this level
  const properties: MVELCompletionItem[] = [];
  Object.entries(current).forEach(([key, value]: [string, any]) => {
    properties.push({
      label: key,
      kind: 'Property',
      insertText: key,
      detail: `${value.type}${value.description ? ` - ${value.description}` : ''}`,
      documentation: `Property: ${path}.${key} (${value.type})`
    });
  });

  return properties;
}

export function getVariableTypeFromPath(path: string, schema?: any): string {
  if (!schema?.properties) return 'any';

  const parts = path.split('.');
  let current = schema.properties;

  for (const part of parts) {
    if (!current[part]) return 'any';
    current = current[part];

    if (current.type === 'object' && current.properties) {
      current = current.properties;
    } else {
      return current.type || 'any';
    }
  }

  return 'any';
}

export function getLocalVariableType(varName: string, code: string): string {
  const lines = code.split('\n');

  for (const line of lines) {
    // Check for map literals
    if (line.includes(`${varName} =`) && line.includes('[') && line.includes(':')) {
      return 'map';
    }
    // Check for array literals
    if (line.includes(`${varName} =`) && line.includes('[') && !line.includes(':')) {
      return 'array';
    }
    // Check for string literals
    if (line.includes(`${varName} =`) && (line.includes('"') || line.includes("'"))) {
      return 'string';
    }
    // Check for number literals
    if (line.includes(`${varName} =`) && /\d/.test(line)) {
      return 'number';
    }
  }

  return 'unknown';
}

export function getPropertiesForLocalVariable(
  varName: string,
  type: string,
  code: string
): MVELCompletionItem[] {
  const properties: MVELCompletionItem[] = [];

  if (type === 'map') {
    // For maps, try to extract keys from the definition
    const lines = code.split('\n');
    for (const line of lines) {
      if (line.includes(`${varName} =`) && line.includes('[')) {
        const mapMatch = line.match(/\[(.*?)\]/);
        if (mapMatch) {
          const mapContent = mapMatch[1];
          const keyMatches = mapContent.match(/"([^"]+)"|'([^']+)'|(\w+)\s*:/g);
          if (keyMatches) {
            keyMatches.forEach((match) => {
              const key = match.replace(/["':\s]/g, '');
              properties.push({
                label: key,
                kind: 'Property',
                insertText: key,
                detail: 'Map property',
                documentation: `Map property: ${key}`
              });
            });
          }
        }
        break;
      }
    }
  }

  return properties;
}

export function getMonacoCompletionKind(
  kind: string,
  monaco: typeof import('monaco-editor')
): monaco.languages.CompletionItemKind {
  switch (kind.toLowerCase()) {
    case 'keyword':
      return monaco.languages.CompletionItemKind.Keyword;
    case 'function':
      return monaco.languages.CompletionItemKind.Function;
    case 'method':
      return monaco.languages.CompletionItemKind.Method;
    case 'property':
      return monaco.languages.CompletionItemKind.Property;
    case 'variable':
      return monaco.languages.CompletionItemKind.Variable;
    default:
      return monaco.languages.CompletionItemKind.Text;
  }
}

export function getLocalVariables(code: string): string[] {
  const variables: string[] = [];
  const lines = code.split('\n');

  lines.forEach((line) => {
    // Match variable assignments: varName = value
    const assignments = line.match(/(\w+)\s*=/g);
    if (assignments) {
      assignments.forEach((assignment) => {
        const varName = assignment.replace(/\s*=/g, '');
        if (!variables.includes(varName)) {
          variables.push(varName);
        }
      });
    }

    // Match foreach loops: foreach (item : collection)
    const foreachMatch = line.match(/foreach\s*\(\s*(\w+)\s*:/);
    if (foreachMatch) {
      const varName = foreachMatch[1];
      if (!variables.includes(varName)) {
        variables.push(varName);
      }
    }
  });

  return variables;
}

// Export all enhanced features
export const mvelBuiltins = mvelBuiltinsEnhanced;
export const arrayMethods = enhancedArrayMethods;
export const mapMethods = enhancedMapMethods;
export const stringMethods = enhancedStringMethods;
export const getMethodsForType = getMethodsForTypeEnhanced;
export const createCompletionFromSchema = createCompletionFromSchemaEnhanced;
